/**
 * AuthPage — full-screen branded sign-in/sign-up wrapper.
 * Replaces the plain white Amplify box with a dark, on-brand experience.
 * Includes a visible "← Back to home" escape link.
 */
import { Link } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Zap, ArrowLeft, Shield, CheckCircle2 } from "lucide-react";

const TRUST_ITEMS = [
  "NHS & SACN-aligned recommendations",
  "UK data residency — GDPR compliant",
  "No PII shared with third parties",
  "Free plan — no credit card needed",
];

export default function AuthPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-ay-bg flex flex-col" style={{ background: "#080c14" }}>
      {/* Grid background */}
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <div className="orb orb-cyan   w-[500px] h-[500px] -top-40 -left-40 opacity-20 pointer-events-none" />
      <div className="orb orb-violet w-[400px] h-[400px] bottom-0 right-0 opacity-15 pointer-events-none" />

      {/* Top bar — back link + logo */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4 border-b border-ay-border/50">
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-ay-muted hover:text-white transition-colors group"
        >
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to home
        </Link>

        <Link to="/" className="flex items-center gap-2.5">
          <div className="relative w-7 h-7">
            <div className="absolute inset-0 rounded-lg bg-ay-cyan/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Zap size={14} className="text-ay-cyan" />
            </div>
          </div>
          <span className="font-bold text-base">
            <span className="text-white">Alpha</span>
            <span className="text-ay-cyan">You</span>
          </span>
        </Link>

        {/* Spacer to centre logo */}
        <div className="w-24" />
      </header>

      {/* Main content */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-12 items-center">

          {/* Left: value proposition */}
          <div className="hidden lg:block">
            <div className="badge-cyan mb-6">
              <Shield size={11} /> Trusted by UK health-conscious individuals
            </div>
            <h1 className="text-4xl font-black text-white leading-tight mb-4">
              Know your body.<br />
              <span className="text-gradient-cyan">Own your health.</span>
            </h1>
            <p className="text-slate-400 leading-relaxed mb-8">
              Join thousands of UK users who've discovered exactly which vitamins
              their body needs — based on their biology, lifestyle, and UK clinical guidelines.
            </p>
            <ul className="space-y-3">
              {TRUST_ITEMS.map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 size={15} className="text-ay-emerald flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Amplify auth form — dark-themed via CSS overrides */}
          <div className="w-full max-w-sm mx-auto lg:mx-0">
            {/* Amplify custom header */}
            <div className="text-center mb-6">
              <p className="text-ay-muted text-sm">Sign in or create your free account</p>
            </div>

            {/* The Amplify component — styled via global CSS overrides in index.css */}
            <div className="auth-wrapper">
              {children}
            </div>

            <p className="text-center text-xs text-ay-muted mt-6 leading-relaxed">
              By continuing, you agree to our{" "}
              <Link to="/" className="text-ay-cyan hover:underline">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/" className="text-ay-cyan hover:underline">Privacy Policy</Link>.
              <br />Not medical advice. Always consult your GP.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
